import { BaseLayer } from './base-layer'
import {
    SHADER_CONTROL_KEY_BRIGHTNESS,
    SHADER_CONTROL_KEY_COLOR,
    SHADER_CONTROL_KEY_CONTRAST,
    SHADER_CONTROL_KEY_NORMALIZE,
} from '../../utils/shaders'
import {
    NeuroglancerImageUserLayer,
    NeuroglancerWatchableValueInterface,
} from '../neuroglancer-type-mappings'
import { GenericLayer } from './generic-layer'
import { LayerType } from './layer-types'
import {
    hexStringToRgbArray,
    percentToValue,
    rgbArrayToHexString,
    valueToPercent,
} from '../../utils/color-utils'
import { defaultDebounceTimeSkipFirst } from '../../utils/rxjs-pipelines'
import { LayerPropertyStoreConnector } from '../../utils/layer-property-store-connector'
import { Store } from '@ngrx/store'
import {
    TrackableService,
    WatchableValue,
} from '../../../case-viewer/neuroglancer-frame/trackable.service'
import { ImageInvlerpParameters } from 'neuroglancer/webgl/shader_ui_controls'
import { selectLayers } from '../../../state/viewer/viewer.selectors'
import { firstValueFrom } from 'rxjs'

export class ImageLayer extends BaseLayer implements GenericLayer {
    readonly type = LayerType.Image

    readonly opacityPercent: WatchableValue<number>
    readonly opacityUnselectedPercent = null
    readonly saturationPercent = null
    readonly segmentationColor = null

    shaderColor: WatchableValue<string> | null = null
    brightness: WatchableValue<number> | null = null
    contrast: WatchableValue<number> | null = null
    normalize: WatchableValue<ImageInvlerpParameters> | null = null

    constructor(
        private readonly layer: NeuroglancerImageUserLayer,
        store: Store,
        trackable: TrackableService
    ) {
        const storeConnectors = new LayerPropertyStoreConnector(store)
            .addVisibility()
            .addOpacity()
            .addShaderColor()
            .addBrightness()
            .addContrast()
            .addNormalize()
            .build()
        super(layer.managedLayer, storeConnectors, trackable)

        this.opacityPercent = trackable.wrapWatchableValueWithConversion(layer.opacity, {
            fromNeuroglancer: valueToPercent,
            toNeuroglancer: percentToValue,
        })
        if (storeConnectors.dispatchOpacity) {
            this.opacityPercent.value$.pipe(defaultDebounceTimeSkipFirst()).subscribe(opacity => {
                storeConnectors.dispatchOpacity?.(this.name, percentToValue(opacity))
            })
        }

        trackable.observeEvent(layer.readyStateChanged).subscribe(() => {
            const colorTrackable =
                this.getShaderControlTrackable<Float32Array>(SHADER_CONTROL_KEY_COLOR)
            if (colorTrackable && !this.shaderColor) {
                this.shaderColor = trackable.wrapWatchableValueWithConversion(colorTrackable, {
                    fromNeuroglancer: rgbArrayToHexString,
                    toNeuroglancer: hexStringToRgbArray,
                })
                this.shaderColor.value$.pipe(defaultDebounceTimeSkipFirst()).subscribe(color => {
                    storeConnectors.dispatchShaderColor?.(this.name, color)
                })
            }

            const brightnessTrackable = this.getShaderControlTrackable<number>(
                SHADER_CONTROL_KEY_BRIGHTNESS
            )
            if (brightnessTrackable && !this.brightness) {
                this.brightness = trackable.wrapWatchableValue(brightnessTrackable)
                if (storeConnectors.dispatchBrightness) {
                    this.brightness.value$
                        .pipe(defaultDebounceTimeSkipFirst())
                        .subscribe(brightness => {
                            storeConnectors.dispatchBrightness?.(this.name, brightness)
                        })
                }
            }

            const contrastTrackable = this.getShaderControlTrackable<number>(
                SHADER_CONTROL_KEY_CONTRAST
            )
            if (contrastTrackable && !this.contrast) {
                this.contrast = trackable.wrapWatchableValue(contrastTrackable)

                if (storeConnectors.dispatchContrast) {
                    this.contrast.value$
                        .pipe(defaultDebounceTimeSkipFirst())
                        .subscribe(contrast => {
                            storeConnectors.dispatchContrast?.(this.name, contrast)
                        })
                }
            }

            const normalizeTrackable = this.getShaderControlTrackable<ImageInvlerpParameters>(
                SHADER_CONTROL_KEY_NORMALIZE
            )

            if (normalizeTrackable && !this.normalize) {
                firstValueFrom(store.select(selectLayers)).then(layers => {
                    const layerConfig = layers?.find(l => l.name === this.name)
                    if (layerConfig?.normalize) {
                        normalizeTrackable.value = layerConfig.normalize
                    }
                })

                this.normalize = trackable.wrapWatchableValue(normalizeTrackable)
                if (storeConnectors.dispatchNormalize) {
                    this.normalize.value$
                        .pipe(defaultDebounceTimeSkipFirst())
                        .subscribe(normalize => {
                            storeConnectors.dispatchNormalize?.(this.name, normalize)
                        })
                }
            }
        })
    }

    getShaderControlTrackable<T>(key: string): NeuroglancerWatchableValueInterface<T> | undefined {
        return this.layer.shaderControlState.value.get(key)?.trackable
    }
}
